/**
 * Bulk actions and undo — DESIGN.md §7.7 (R31, R40, D18).
 *
 * "Triage is a batch activity — re-prioritising a backlog or redistributing a
 * departing dev's cards must not be forty one-by-one edits."
 *
 * Two rules from §7.7 shape these schemas:
 *  - every action runs **per card through the normal service layer**, so
 *    partial failure is normal and is reported per card;
 *  - every action returns an **undo token**, because "bulk actions without a
 *    working undo are a footgun".
 */
import { z } from 'zod';
import { BULK_MAX_TASKS } from '../constants.js';
import { PRIORITIES } from '../enums.js';
import { dateSchema, uuidSchema } from './common.js';
/** The actions the floating toolbar offers (§7.7). */
export const bulkActionSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('set_status'), statusId: uuidSchema }),
    z.object({ type: z.literal('set_assignee'), assigneeId: uuidSchema.nullable() }),
    z.object({ type: z.literal('set_due_date'), dueDate: dateSchema.nullable() }),
    z.object({ type: z.literal('set_priority'), priority: z.enum(PRIORITIES).nullable() }),
    z.object({ type: z.literal('add_tags'), tagIds: z.array(uuidSchema).min(1).max(50) }),
    z.object({ type: z.literal('remove_tags'), tagIds: z.array(uuidSchema).min(1).max(50) }),
    z.object({
        type: z.literal('move_to_section'),
        projectId: uuidSchema,
        sectionId: uuidSchema.nullable(),
    }),
    z.object({ type: z.literal('add_to_project'), projectId: uuidSchema }),
    z.object({
        type: z.literal('add_collaborators'),
        userIds: z.array(uuidSchema).max(100).default([]),
        groupIds: z.array(uuidSchema).max(50).default([]),
    }),
    z.object({ type: z.literal('complete'), completed: z.boolean() }),
    z.object({ type: z.literal('delete') }),
]);
export const bulkRequestSchema = z
    .object({
    taskIds: z.array(uuidSchema).min(1).max(BULK_MAX_TASKS),
    action: bulkActionSchema,
    /** §6.5 — asked once for the whole batch, not per blocked card. */
    force: z.boolean().default(false),
})
    .strict();
export const bulkResultSchema = z.object({
    updated: z.number().int().nonnegative(),
    skipped: z.array(z.object({
        id: uuidSchema,
        number: z.number().int().positive().nullable(),
        reason: z.string(),
    })),
    /**
     * R40/D18 — redeemable for ten minutes. Null when nothing changed, so the
     * toast does not offer an undo that would do nothing.
     */
    undoToken: z.string().nullable(),
    /** What the toast says: "Brad updated 17 cards". */
    summary: z.string(),
});
export const undoRequestSchema = z.object({ undoToken: z.string().min(8) }).strict();
export const undoResultSchema = z.object({
    restored: z.number().int().nonnegative(),
    skipped: z.array(z.object({ id: uuidSchema, reason: z.string() })),
    summary: z.string(),
});
/** §7.2 (R36) — CSV export of the current view. */
export const exportRequestSchema = z
    .object({
    config: z.unknown(),
    /** Visible columns, in order. Defaults to the config's own columns. */
    columns: z.array(z.string()).max(40).optional(),
})
    .passthrough();
//# sourceMappingURL=bulk.js.map